/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: Moment library.
import Debounce from 'lodash/debounce' // Npm: lodash library.
import _ from 'underscore' // Npm: underscore library.
import { useQuery } from '@apollo/client' // Npm: Apollo client.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import DownloadToExcel from 'components/DownloadToExcel'
import TableSpinner from 'components/TableSpinner'
import Modal from 'components/Modal'
import IpDirectoryUpsert from 'components/IpDirectoryUpsert'
import IpDirectoryDropDownOptions from 'components/IpDirectoryDropDownOptions'
import IpDirectoryDelete from 'components/IpDirectoryDelete'


/*
 * STYLES
 */
import {
  buttonStyle
} from './index.style'


/*
 * GRAPHS
 */
import IpDirectoryReadQuery from './__query__/index.ipDirectory.read.query'


/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _skipDifference = 8
  const _successFlags = Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key)

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': _skipDifference,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({ items: [] })
  const [searchQuery, setSearchQuery] = React.useState('')
  const { 'isOpen': isIpDirectoryCreateOpen, 'onOpen': onIpDirectoryCreateOpen, 'onClose': onIpDirectoryCreateClose } = useDisclosure()
  const { 'isOpen': isIpDirectoryUpdateOpen, 'onOpen': onIpDirectoryUpdateOpen, 'onClose': onIpDirectoryUpdateClose } = useDisclosure()
  const { 'isOpen': isIpDirectoryDeleteOpen, 'onOpen': onIpDirectoryDeleteOpen, 'onClose': onIpDirectoryDeleteClose } = useDisclosure()
  const _QueryIpDirectoryRead = useQuery(IpDirectoryReadQuery, {
    'variables': {
      'ipDirectoryToSearch': searchQuery,
      'skip': paginationModel?.pageSize * paginationModel?.page,
      'take': paginationModel?.pageSize
    },
    'fetchPolicy': Object.React.App.fetchPolicy,
    'pollInterval': Object.React.App.pollInterval
  })
  const _isFirstLoadCompleted = React.useRef(false)
  const _onSearchInputChange = React.useRef(_.debounce((value) => setSearchQuery(value), 800)).current
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({ 'base': 'false', 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })

  // Data loading check.
  if (!_QueryIpDirectoryRead.loading && 0 < _QueryIpDirectoryRead.data?.IpDirectoryRead?.length && !_isFirstLoadCompleted.current) _isFirstLoadCompleted.current = true
  if (!_.every(_.pluck(_QueryIpDirectoryRead.data?.IpDirectoryRead, 'status'), i => _successFlags.includes(i))) _isFirstLoadCompleted.current = false

  // Extract search from filter model
  React.useEffect(() => {
    const displayNameFilter = filterModel?.items?.find(item => item.field === 'displayName')
    const searchValue = displayNameFilter?.value || ''
    _onSearchInputChange(searchValue)
  }, [filterModel])

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      _onSearchInputChange.cancel?.()
    }
  }, [])

  // Data assignment.
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S.No.',
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 180
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'headerAlign': 'center',
      'hide': true
    },
    {
      'field': 'displayName',
      'headerName': 'Display Name',
      'description': 'Display name of IP directory.',
      'editable': false,
      'sortable': false,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'width': 350,
    },
    {
      'field': 'totalIps',
      'headerName': 'Total Ips',
      'description': 'Total number of IPs.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 250,
    },
    {
      'field': 'createdAt',
      'headerName': 'Created At',
      'description': 'Created date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 250,
    },
    {
      'field': 'updatedAt',
      'headerName': 'Updated At',
      'description': 'Updated date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'disableColumnMenu': true,
      'headerAlign': 'center',
      'width': 250,
    },
    {
      'field': 'action',
      'headerName': 'Actions',
      'description': 'Actions for IP directory.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 150,
      'disableColumnMenu': true,
      'renderCell': ({ row }) => <IpDirectoryDropDownOptions
        ipDirectoryId={row?.id}
        onIpDirectoryUpdate={onIpDirectoryUpdateOpen}
        onIpDirectoryDelete={onIpDirectoryDeleteOpen}
      />
    }
  ], [onIpDirectoryUpdateOpen, onIpDirectoryDeleteOpen])

  const rows = React.useMemo(() => _.compact(_QueryIpDirectoryRead?.data?.IpDirectoryRead?.map((r, index) => _successFlags.includes(r.status) ? ({
    'id': r?.id || String.random(9),
    'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
    'displayName': r?.displayName ?? '-',
    'totalIps': r?._count?.Ip ?? 0,
    'createdAt': Moment(r?.createdAt).format('YYYY-MM-DD'),
    'updatedAt': Moment(r?.updatedAt).format('YYYY-MM-DD')
  }) : null)), [_QueryIpDirectoryRead?.data?.IpDirectoryRead, paginationModel.page, paginationModel.pageSize])

  // Return component.
  return (
    <>
      <Flex className='ipDirectory base'>
        <Flex
          display='flex'
          flex={1}
          flexDirection='column'
          gap={_isCurrentViewMobile ? '12px' : '22px'}
          bg='white'
          minH="81vh"
          maxH='81vh'
          borderRadius='20px'
          p={_isCurrentViewMobile ? '12px' : '22px'}>
          <Flex pb='0px' justify='space-between'>
            <Flex gap='12px'>
              <Tooltip label='Create New Folder' fontSize='sm'>
                <Button
                  leftIcon={<IoMdAdd />}
                  onClick={onIpDirectoryCreateOpen}
                  style={buttonStyle}>
                  Create New
                </Button>
              </Tooltip>
            </Flex>
            <DownloadToExcel
              cellsData={0 === _QueryIpDirectoryRead?.data?.IpDirectoryRead?.length || !_.every(_.pluck(_QueryIpDirectoryRead?.data?.IpDirectoryRead, 'status'), i => _successFlags.includes(i)) ? [] : _QueryIpDirectoryRead?.data?.IpDirectoryRead.map((item, __index) => ({
                'S.No.': `${(__index + (paginationModel.pageSize * (paginationModel.page))) + 1}.`,
                'CreatedAt': Moment(item?.createdAt).format('YYYY-MM-DD'),
                'UpdatedAt': Moment(item?.updatedAt).format('YYYY-MM-DD'),
                'Display Name': item.displayName ?? '-',
                'Total Ips': item?._count?.Ip ?? 0
              }))}
              headersData={[
                'S.No.',
                'CreatedAt',
                'UpdatedAt',
                'Display Name',
                'Total Ips'
              ].map(i => ({ 'key': i, 'label': i }))} />
          </Flex>
          <DataGrid
            key={`${paginationModel.page}-${paginationModel.pageSize}`}
            rows={rows}
            columns={columns}
            rowHeight={58}
            rowCount={_.pluck(_QueryIpDirectoryRead?.data?.IpDirectoryRead, '_totalCount')?.[0] || 0}
            paginationMode='server'
            disableColumnSorting={true}
            filterModel={filterModel}
            onFilterModelChange={setFilterModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[8, 10, 30, 50, 100]}
            slots={{
              'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            loading={_QueryIpDirectoryRead?.loading}
          />
        </Flex>
        <Modal
          size='md'
          title='Ip Directory Create'
          isOpen={isIpDirectoryCreateOpen}
          onClose={onIpDirectoryCreateClose}>
          <IpDirectoryUpsert isCreateOnly={true} />
        </Modal>
        <Modal
          size='md'
          title='Ip Directory Update'
          isOpen={isIpDirectoryUpdateOpen}
          onClose={onIpDirectoryUpdateClose}>
          <IpDirectoryUpsert />
        </Modal>
        <Modal
          size='md'
          title='Ip Directory Delete'
          isOpen={isIpDirectoryDeleteOpen}
          onClose={onIpDirectoryDeleteClose}>
          <IpDirectoryDelete />
        </Modal>
      </Flex>
    </>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {}


/*
 * EXPORT
 */
export default Index
